<template>
  <div class="entries-container">
    <!-- Cabeçalho da página -->
    <div class="page-header">
      <h1 class="page-title">
        <i class="fa-solid fa-chart-bar"></i>
        Fechamento Coletivo
      </h1>
      <p class="page-subtitle">Consulte o balanço consolidado das regiões</p>
    </div>

    <!-- Filtros -->
    <div class="filters-card">
      <div class="card-header">
        <h3 class="card-title">
          <i class="fa-solid fa-filter"></i>
          Filtros de Consulta
        </h3>
      </div>
      
      <div class="filters-content">
        <div class="filters-grid">
          <div class="input-group">
            <label class="input-label">
              <i class="fa-solid fa-map-location-dot"></i>
              Regiões *
            </label>
            <div class="multiselect-container">
              <MultiSelect 
                v-model="selectedRegions" 
                :options="availableRegions" 
                placeholder="Selecione as regiões"
                :showSelectAllButtons="true"
              />
            </div>
          </div>

          <div class="input-group">
            <label class="input-label">
              <i class="fa-solid fa-calendar-days"></i>
              Data Início *
            </label>
            <input 
              class="form-input" 
              type="date" 
              v-model="balanceDateBegin"
              placeholder="Data de início"
            />
          </div>

          <div class="input-group">
            <label class="input-label">
              <i class="fa-solid fa-calendar-days"></i>
              Data Fim *
            </label>
            <input 
              class="form-input" 
              type="date" 
              v-model="balanceDateEnd"
              placeholder="Data de fim"
            />
          </div>
        </div>

        <div class="filters-actions">
          <button 
            type="button" 
            class="btn-primary" 
            @click="searchBalance()"
            :disabled="loading"
          >
            <i v-if="loading" class="fa-solid fa-spinner fa-spin"></i>
            <i v-else class="fa-solid fa-search"></i>
            {{ loading ? 'Consultando...' : 'Pesquisar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-show="loading" class="loading-container">
      <div class="loading-spinner">
        <i class="fa-solid fa-spinner fa-spin"></i>
        <span>Carregando dados...</span>
      </div>
    </div>

    <!-- Tabela Principal: Fechamento Coletivo -->
    <div v-show="!loading && balance && balance.regions && balance.regions.length > 0" class="results-card">
      <div class="card-header">
        <h3 class="card-title">
          <i class="fa-solid fa-table"></i>
          Fechamento por Região
        </h3>
        <div class="card-actions">
          <div class="table-zoom-controls" title="Zoom da tabela">
            <button
              type="button"
              class="btn-zoom"
              :disabled="tableZoom <= 5"
              @click="decreaseTableZoom"
              title="Diminuir zoom"
            >
              <i class="fa-solid fa-magnifying-glass-minus"></i>
            </button>
            <span class="zoom-label" @click="resetTableZoom" title="Restaurar 100%">
              {{ tableZoom }}%
            </span>
            <button
              type="button"
              class="btn-zoom"
              :disabled="tableZoom >= 100"
              @click="increaseTableZoom"
              title="Aumentar zoom"
            >
              <i class="fa-solid fa-magnifying-glass-plus"></i>
            </button>
          </div>
          <div class="columns-dropdown" ref="columnsDropdown">
            <button
              type="button"
              class="btn-action btn-columns"
              @click.stop="showColumnsMenu = !showColumnsMenu"
              title="Mostrar ou esconder colunas"
            >
              <i class="fa-solid fa-columns"></i>
              Colunas
              <i class="fa-solid fa-caret-down columns-caret"></i>
            </button>
            <div v-if="showColumnsMenu" class="columns-menu" @click.stop>
              <div class="columns-menu-header">
                <span>Colunas visíveis</span>
                <button type="button" class="columns-reset" @click="resetVisibleColumns">
                  Restaurar
                </button>
              </div>
              <label
                v-for="col in toggleableColumns"
                :key="col.key"
                class="columns-menu-item"
                :class="{ locked: col.locked }"
              >
                <input
                  type="checkbox"
                  :checked="isColumnVisible(col.key)"
                  :disabled="col.locked"
                  @change="toggleColumn(col.key)"
                />
                <span>{{ col.label }}</span>
              </label>
            </div>
          </div>
          <button 
            type="button" 
            class="btn-action btn-print" 
            @click="printReport()"
            title="Imprimir relatório"
          >
            <i class="fa-solid fa-print"></i>
            Imprimir
          </button>
          <button 
            type="button" 
            class="btn-action btn-pdf" 
            @click="exportToPDF()"
            title="Exportar para PDF"
          >
            <i class="fa-solid fa-file-pdf"></i>
            PDF
          </button>
          <button 
            type="button" 
            class="btn-action btn-excel" 
            @click="exportToExcel()"
            title="Exportar para Excel"
          >
            <i class="fa-solid fa-file-excel"></i>
            Excel
          </button>
        </div>
      </div>
      
      <div class="table-container" :style="tableZoomStyle">
        <table class="modern-table">
          <thead>
            <tr>
              <th v-if="isColumnVisible('region')">Região</th>
              <th v-if="isColumnVisible('previousBalance')">Saldo Anterior</th>
              <th v-if="isColumnVisible('bet')">Vendas(+)</th>
              <th v-if="isColumnVisible('comission')">Comissão(-)</th>
              <th v-if="isColumnVisible('net')">Líquido</th>
              <th v-if="isColumnVisible('retainedPrizes')">Prêmios Retidos</th>
              <th v-if="isColumnVisible('totalAwarded')">Prêmios(-)</th>
              <th v-if="isColumnVisible('liquidoTwo')">{{ getLiquidoTwoColumnTitle }}</th>
              <th v-if="isColumnVisible('retorno')">Retorno</th>
              <th v-if="isColumnVisible('acertoLp')">Acerto L.P</th>
              <th v-if="isColumnVisible('bancaRecebeu')">Banca Recebeu(-)</th>
              <th v-if="isColumnVisible('bancaPagou')">Banca Pagou(+)</th>
              <th v-if="isColumnVisible('currentBalance')">Saldo Atual</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="r in balance.regions" 
              :key="r.regionId"
              class="data-row"
            >
              <td v-if="isColumnVisible('region')">
                <div class="region-cell" @click="openBets(r.regionId)">
                  <i class="fa-solid fa-map-marker-alt"></i>
                  <span class="region-link">{{ r.regionDescription }}</span>
                </div>
              </td>
              <td v-if="isColumnVisible('previousBalance')">
                <span :class="['currency-value', { 'currency-positive': r.previousBalance > 0, 'currency-negative': r.previousBalance < 0 }]">{{ setCurrencyString(r.previousBalance) }}</span>
              </td>
              <td v-if="isColumnVisible('bet')">
                <span
                  v-if="(r.bet || 0) > 0"
                  class="currency-value currency-positive clickable-value"
                  @click="openSalesModal(r)"
                  title="Clique para ver as vendas"
                >
                  {{ setCurrencyString(r.bet) }}
                </span>
                <span v-else class="currency-value currency-positive">{{ setCurrencyString(r.bet) }}</span>
              </td>
              <td v-if="isColumnVisible('comission')">
                <span class="currency-value currency-negative">{{ setCurrencyString(r.comission) }}</span>
              </td>
              <td v-if="isColumnVisible('net')">
                <span :class="['currency-value', { 'currency-positive': r.net > 0, 'currency-negative': r.net < 0 }]">{{ setCurrencyString(r.net) }}</span>
              </td>
              <td v-if="isColumnVisible('retainedPrizes')">
                <span class="currency-value currency-negative">{{ setCurrencyString(r.retainedPrizes) }}</span>
              </td>
              <td v-if="isColumnVisible('totalAwarded')">
                <span
                  v-if="(r.totalAwarded || 0) > 0"
                  class="currency-value currency-negative clickable-value"
                  @click="openPrizeModal(r)"
                  title="Clique para ver bilhetes premiados"
                >
                  {{ setCurrencyString(r.totalAwarded) }}
                </span>
                <span v-else class="currency-value currency-negative">{{ setCurrencyString(r.totalAwarded) }}</span>
              </td>
              <td v-if="isColumnVisible('liquidoTwo')">
                <span :class="['currency-value', { 'currency-positive': calculateLiquidoTwo(r) > 0, 'currency-negative': calculateLiquidoTwo(r) < 0 }]">{{ setCurrencyString(calculateLiquidoTwo(r)) }}</span>
              </td>
              <td v-if="isColumnVisible('retorno')">
                <span class="currency-value">{{ setCurrencyString(calculateReturnValue(r)) }}</span>
              </td>
              <td v-if="isColumnVisible('acertoLp')">
                <span :class="['currency-value', { 'currency-positive': calculateAcertoLp(r) > 0, 'currency-negative': calculateAcertoLp(r) < 0 }]">{{ setCurrencyString(calculateAcertoLp(r)) }}</span>
              </td>
              <td v-if="isColumnVisible('bancaRecebeu')">
                <span 
                  v-if="shouldShowBancaRecebeuInMainTable(r)"
                  :class="['currency-value', 'clickable-value', shouldUseInvertedBancaStylingInMainTable(r) ? 'currency-negative' : 'currency-positive']"
                  @click="openTransactionsModal(r, shouldUseInvertedBancaStylingInMainTable(r) ? 'debit' : 'credit', false)"
                  title="Clique para ver detalhes"
                >
                  {{ setCurrencyString(getBancaRecebeuMainTable(r)) }}
                </span>
                <span v-else class="currency-value currency-muted">—</span>
              </td>
              <td v-if="isColumnVisible('bancaPagou')">
                <span 
                  v-if="shouldShowBancaPagouInMainTable(r)"
                  :class="['currency-value', 'clickable-value', shouldUseInvertedBancaStylingInMainTable(r) ? 'currency-positive' : 'currency-negative']"
                  @click="openTransactionsModal(r, shouldUseInvertedBancaStylingInMainTable(r) ? 'credit' : 'debit', false)"
                  title="Clique para ver detalhes"
                >
                  {{ setCurrencyString(getBancaPagouMainTable(r)) }}
                </span>
                <span v-else class="currency-value currency-muted">—</span>
              </td>
              <td v-if="isColumnVisible('currentBalance')">
                <span :class="['currency-value', { 'currency-negative': r.currentBalance < 0, 'currency-positive': r.currentBalance > 0 }]">
                  {{ setCurrencyString(r.currentBalance) }}
                </span>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="total-row">
              <td v-if="isColumnVisible('region')">
                <div class="total-cell">
                  <i class="fa-solid fa-calculator"></i>
                  <span>Total</span>
                </div>
              </td>
              <td v-if="isColumnVisible('previousBalance')">
                <span :class="['currency-value', { 'currency-positive': balance?.previousBalanceAmount > 0, 'currency-negative': balance?.previousBalanceAmount < 0 }]">{{ setCurrencyString(balance?.previousBalanceAmount ?? 0) }}</span>
              </td>
              <td v-if="isColumnVisible('bet')">
                <span class="currency-value currency-positive">{{ setCurrencyString(balance?.betAmount ?? 0) }}</span>
              </td>
              <td v-if="isColumnVisible('comission')">
                <span class="currency-value currency-negative">{{ setCurrencyString(balance?.comissionAmount ?? 0) }}</span>
              </td>
              <td v-if="isColumnVisible('net')">
                <span :class="['currency-value', { 'currency-positive': balance?.netAmount > 0, 'currency-negative': balance?.netAmount < 0 }]">{{ setCurrencyString(balance?.netAmount ?? 0) }}</span>
              </td>
              <td v-if="isColumnVisible('retainedPrizes')">
                <span class="currency-value currency-negative">{{ setCurrencyString(balance?.retainedPrizes ?? 0) }}</span>
              </td>
              <td v-if="isColumnVisible('totalAwarded')">
                <span class="currency-value currency-negative">{{ setCurrencyString(balance?.totalAwarded ?? 0) }}</span>
              </td>
              <td v-if="isColumnVisible('liquidoTwo')">
                <span :class="['currency-value', { 'currency-positive': calculateTotalLiquidoTwo() > 0, 'currency-negative': calculateTotalLiquidoTwo() < 0 }]">{{ setCurrencyString(calculateTotalLiquidoTwo()) }}</span>
              </td>
              <td v-if="isColumnVisible('retorno')">
                <span class="currency-value">{{ setCurrencyString(calculateTotalReturnValue()) }}</span>
              </td>
              <td v-if="isColumnVisible('acertoLp')">
                <span :class="['currency-value', { 'currency-positive': this.calculateTotalAcertoLp() > 0, 'currency-negative': this.calculateTotalAcertoLp() < 0 }]">{{ setCurrencyString(this.calculateTotalAcertoLp()) }}</span>
              </td>
              <td v-if="isColumnVisible('bancaRecebeu')">
                <span :class="['currency-value', { 'currency-positive': calculateTotalBancaRecebeuMain() >= 0, 'currency-negative': calculateTotalBancaRecebeuMain() < 0 }]">{{ setCurrencyString(calculateTotalBancaRecebeuMain()) }}</span>
              </td>
              <td v-if="isColumnVisible('bancaPagou')">
                <span :class="['currency-value', { 'currency-positive': calculateTotalBancaPagouMain() >= 0, 'currency-negative': calculateTotalBancaPagouMain() < 0 }]">{{ setCurrencyString(calculateTotalBancaPagouMain()) }}</span>
              </td>
              <td v-if="isColumnVisible('currentBalance')">
                <span :class="['currency-value', 'final-balance', { 'currency-negative': balance?.currentBalanceAmount < 0, 'currency-positive': balance?.currentBalanceAmount > 0 }]">
                  {{ setCurrencyString(balance?.currentBalanceAmount ?? 0) }}
                </span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!-- Tabela Acerto L.P -->
    <div v-show="!loading && balance && balance.regions && balance.regions.length > 0" class="results-card">
      <div class="card-header">
        <h3 class="card-title">
          <i class="fa-solid fa-coins"></i>
          Acerto L.P
        </h3>
      </div>
      
      <div class="table-container" :style="tableZoomStyle">
        <table class="modern-table">
          <thead>
            <tr>
              <th>Região</th>
              <th>Acerto L.P</th>
              <th>Banca Recebeu(+)</th>
              <th>Banca Pagou(-)</th>
              <th>Saldo Atual</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="r in balance.regions" 
              :key="r.regionId"
              v-show="shouldShowRegionInAcertoLp(r)"
              class="data-row"
            >
              <td>
                <div class="region-cell">
                  <i class="fa-solid fa-map-marker-alt"></i>
                  <span>{{ r.regionDescription }}</span>
                </div>
              </td>
              <td>
                <span :class="['currency-value', { 'currency-positive': calculateAcertoLp(r) > 0, 'currency-negative': calculateAcertoLp(r) < 0 }]">{{ setCurrencyString(calculateAcertoLp(r)) }}</span>
              </td>
              <td>
                <span 
                  v-if="getBancaRecebeuAcertoLp(r) > 0"
                  class="currency-value currency-positive clickable-value" 
                  @click="openTransactionsModal(r, 'credit', true)"
                  title="Clique para ver detalhes"
                >
                  {{ setCurrencyString(getBancaRecebeuAcertoLp(r)) }}
                </span>
                <span 
                  v-else
                  class="currency-value currency-muted"
                >
                  {{ setCurrencyString(getBancaRecebeuAcertoLp(r)) }}
                </span>
              </td>
              <td>
                <span 
                  v-if="getBancaPagouAcertoLp(r) > 0"
                  class="currency-value currency-negative clickable-value" 
                  @click="openTransactionsModal(r, 'debit', true)"
                  title="Clique para ver detalhes"
                >
                  {{ setCurrencyString(getBancaPagouAcertoLp(r)) }}
                </span>
                <span 
                  v-else
                  class="currency-value currency-muted"
                >
                  {{ setCurrencyString(getBancaPagouAcertoLp(r)) }}
                </span>
              </td>
              <td>
                <span :class="['currency-value', { 'currency-negative': r.currentBalanceLp < 0, 'currency-positive': r.currentBalanceLp > 0 }]">
                  {{ setCurrencyString(r.currentBalanceLp) }}
                </span>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="total-row">
              <td>
                <div class="total-cell">
                  <i class="fa-solid fa-calculator"></i>
                  <span>Total</span>
                </div>
              </td>
              <td>
                <span :class="['currency-value', { 'currency-positive': this.calculateTotalAcertoLp() > 0, 'currency-negative': this.calculateTotalAcertoLp() < 0 }]">{{ setCurrencyString(this.calculateTotalAcertoLp()) }}</span>
              </td>
              <td>
                <span class="currency-value currency-positive">{{ setCurrencyString(calculateTotalBancaRecebeuAcertoLp()) }}</span>
              </td>
              <td>
                <span class="currency-value currency-negative">{{ setCurrencyString(calculateTotalBancaPagouAcertoLp()) }}</span>
              </td>
              <td>
                <span :class="['currency-value', 'final-balance', { 'currency-negative': balance?.currentBalanceAmountLp < 0, 'currency-positive': balance?.currentBalanceAmountLp > 0 }]">
                  {{ setCurrencyString(balance?.currentBalanceAmountLp ?? 0) }}
                </span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!-- Estado vazio -->
    <div v-show="!loading && (!balance || !balance.regions || balance.regions.length === 0)" class="empty-state">
      <div class="empty-state-content">
        <i class="fa-solid fa-chart-bar"></i>
        <h3>Nenhum dado encontrado</h3>
        <p>Selecione as regiões e o período para consultar o fechamento coletivo</p>
      </div>
    </div>

    <!-- Modal de Detalhes de Transações -->
    <TransactionsDetailModal
      v-if="showTransactionsModal"
      :regionId="modalRegionId"
      :regionName="modalRegionName"
      :transactionType="modalTransactionType"
      :dateBegin="balanceDateBegin"
      :dateEnd="balanceDateEnd"
      :isLpTable="modalIsLpTable"
      :isDescargaRegion="modalIsDescargaRegion"
      @close="closeTransactionsModal"
    />

    <!-- Modal de Bilhetes Premiados / Vendas -->
    <PrizeTicketsModal
      v-if="showPrizeModal || showSalesModal"
      :regionId="ticketsModalRegionId"
      :regionName="ticketsModalEntityName"
      :dateBegin="balanceDateBegin"
      :dateEnd="balanceDateEnd"
      :mode="showSalesModal ? 'sales' : 'prizes'"
      @close="closeTicketsModal"
    />
  </div>
</template>

<script>
import mainService from "@/services/mainService";
import { toast } from "@/composables/useToast";
import { handleErrorMessage } from "@/services/config";
import jsFunctions from "@/services/jsFunctions";
import MultiSelect from "@/components/utils/MultiSelect.vue";
import TransactionsDetailModal from "@/components/TransactionsDetailModal.vue";
import PrizeTicketsModal from "@/components/PrizeTicketsModal.vue";
import { ROLES } from "@/services/permissions";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const ENTRIES_COLUMNS_STORAGE_KEY = "viewEntries.visibleColumns";
const TABLE_ZOOM_STORAGE_KEY = "ab.resultsTableZoom";
const TABLE_ZOOM_MIN = 5;
const TABLE_ZOOM_MAX = 100;
const TABLE_ZOOM_STEP = 5;

const DEFAULT_VISIBLE_COLUMNS = {
  region: true,
  previousBalance: true,
  bet: true,
  comission: true,
  net: true,
  retainedPrizes: true,
  totalAwarded: true,
  liquidoTwo: true,
  retorno: true,
  acertoLp: true,
  bancaRecebeu: true,
  bancaPagou: true,
  currentBalance: true,
};

export default {
  components: { 
    MultiSelect,
    TransactionsDetailModal,
    PrizeTicketsModal
  },
  data: () => ({
    balance: {
      regions: [],
      betAmount: 0
    },
    loading: true,
    regions: [],
    selectedRegions: [],
    balanceDateBegin: "",
    balanceDateEnd: "",
    
    // Controle de usuário e permissões
    currentUser: null,
    userResponsibleRegions: [],
    userBancaId: null,
    bancas: [],
    
    // Controle do modal de detalhes de transações
    showTransactionsModal: false,
    modalRegionId: null,
    modalRegionName: '',
    modalTransactionType: 'credit',
    modalIsLpTable: false,
    modalIsDescargaRegion: false,
    showPrizeModal: false,
    showSalesModal: false,
    ticketsModalRegionId: null,
    ticketsModalEntityName: '',

    // Controle de colunas da tabela principal
    showColumnsMenu: false,
    visibleColumns: { ...DEFAULT_VISIBLE_COLUMNS },
    tableZoom: 100,
  }),
  computed: {
    tableZoomStyle() {
      return { zoom: this.tableZoom / 100 };
    },

    // Garante que selectedRegions seja sempre array (evita erro ao digitar no MultiSelect)
    selectedRegionsSafe() {
      return Array.isArray(this.selectedRegions) ? this.selectedRegions : [];
    },

    currentSelectedRegion() {
      const savedRegion = localStorage.getItem('currentRegion');
      return savedRegion ? JSON.parse(savedRegion) : null;
    },

    currentSelectedBanca() {
      const savedBanca = localStorage.getItem('selectedBanca');
      return savedBanca ? JSON.parse(savedBanca) : null;
    },

    isAdmin() {
      return this.currentUser && this.currentUser.role === ROLES.ADMIN;
    },

    isBanca() {
      return this.currentUser && this.currentUser.role === ROLES.BANCA;
    },

    isGerente() {
      return this.currentUser && this.currentUser.role === ROLES.GERENTE;
    },

    isCobrador() {
      return this.currentUser && this.currentUser.role === ROLES.COBRADOR;
    },

    availableRegions() {
      let regions = this.regions;
      
      if (this.isAdmin) {
        // ADMIN: aplicar filtro por banca selecionada primeiro se houver
        if (this.currentSelectedBanca) {
          regions = regions.filter(region => 
            String(region.bancaId) === String(this.currentSelectedBanca.id)
          );
        }
        
        // Para ADMIN, mostrar todas as regiões (filtradas por banca se selecionada)
        return regions;
      } else {
        // BANCA, GERENTE e COBRADOR: apenas regiões das quais são responsáveis
        regions = regions.filter(region => 
          this.userResponsibleRegions.includes(region.id)
        );
        
        // Para usuários não-admin, aplicar filtro por região ativa selecionada se houver
        if (this.currentSelectedRegion) {
          regions = regions.filter(region => {
            return String(region.id) === String(this.currentSelectedRegion.id);
          });
        }
        
        return regions;
      }
    },

    shouldShowLiquidoTwoColumn() {
      // Mostrar Líquido 2/LP sempre (o título muda baseado no tipo de região)
      return true;
    },

    shouldShowRetornoColumn() {
      // Mostrar Retorno apenas se há pelo menos uma região de descarga
      
      // Primeiro verificar se há dados de balance carregados
      if (!this.balance || !this.balance.regions || this.balance.regions.length === 0) {
        // Se não há dados ainda, verificar pelas regiões selecionadas
        if (this.selectedRegionsSafe.length > 0) {
          return this.selectedRegionsSafe.some(region => {
            const fullRegion = this.regions.find(r => r.id === region.id);
            return fullRegion && fullRegion.onlyDescarga;
          });
        }
        return false;
      }
      
      // Se há dados de balance, verificar se há pelo menos uma região de descarga
      return this.balance.regions.some(region => region.isDescargaRegion);
    },

    shouldShowRetainedPrizesColumn() {
      // Mostrar coluna "Prêmio Retido" apenas para regiões não de descarga (onlyDescarga = false)
      // Se TODAS as regiões têm onlyDescarga = true, então NÃO mostrar "Prêmio Retido"
      if (!this.balance || !this.balance.regions || this.balance.regions.length === 0) {
        // Se não há dados ainda, verificar pelas regiões selecionadas
        if (this.selectedRegionsSafe.length > 0) {
          // Verificar se TODAS as regiões selecionadas são de descarga
          const allSelectedRegionsAreDescarga = this.selectedRegionsSafe.every(region => {
            const fullRegion = this.regions.find(r => r.id === region.id);
            return fullRegion && fullRegion.onlyDescarga === true;
          });
          // Se TODAS são de descarga, NÃO mostrar a coluna
          return !allSelectedRegionsAreDescarga;
        }
        return true; // Mostrar por padrão se não há dados ainda
      }
      
      // Se TODAS as regiões forem de descarga (isDescargaRegion = true), não mostrar a coluna
      const allRegionsAreDescarga = this.balance.regions.every(region => region.isDescargaRegion);
      return !allRegionsAreDescarga;
    },

    shouldShowAcertoLpColumn() {
      // IMPORTANTE: Mostrar coluna de Acerto L.P apenas se há pelo menos uma região de descarga (onlyDescarga = true)
      if (!this.balance || !this.balance.regions || this.balance.regions.length === 0) {
        // Se não há dados ainda, verificar pelas regiões selecionadas
        if (this.selectedRegionsSafe.length > 0) {
          return this.selectedRegionsSafe.some(region => {
            const fullRegion = this.regions.find(r => r.id === region.id);
            return fullRegion && fullRegion.onlyDescarga; // Mostrar apenas se é região de descarga
          });
        }
        return false; // Não mostrar por padrão se não há dados ainda
      }
      
      // Mostrar apenas se há pelo menos uma região de descarga (isDescargaRegion = true)
      return this.balance.regions.some(region => region.isDescargaRegion);
    },

    getLiquidoTwoColumnTitle() {
      // IMPORTANTE: Se todas as regiões NÃO são de descarga, mostrar "LP", senão "Líquido 2"
      if (!this.balance || !this.balance.regions || this.balance.regions.length === 0) {
        // Se não há dados ainda, verificar pelas regiões selecionadas
        if (this.selectedRegionsSafe.length > 0) {
          const allRegionsAreNotDescarga = this.selectedRegionsSafe.every(region => {
            const fullRegion = this.regions.find(r => r.id === region.id);
            return fullRegion && !fullRegion.onlyDescarga; // Se todas NÃO são de descarga
          });
          return allRegionsAreNotDescarga ? "LP" : "Líquido 2";
        }
        return "Líquido 2";
      }
      
      // Se todas as regiões NÃO são de descarga (isDescargaRegion = false), mostrar "LP"
      const allRegionsAreNotDescarga = this.balance.regions.every(region => !region.isDescargaRegion);
      return allRegionsAreNotDescarga ? "LP" : "Líquido 2";
    },

    toggleableColumns() {
      const columns = [
        { key: "region", label: "Região", locked: true },
        { key: "previousBalance", label: "Saldo Anterior" },
        { key: "bet", label: "Vendas(+)" },
        { key: "comission", label: "Comissão(-)" },
        { key: "net", label: "Líquido" },
      ];

      if (this.shouldShowRetainedPrizesColumn) {
        columns.push({ key: "retainedPrizes", label: "Prêmios Retidos" });
      }

      columns.push({ key: "totalAwarded", label: "Prêmios(-)" });

      if (this.shouldShowLiquidoTwoColumn) {
        columns.push({ key: "liquidoTwo", label: this.getLiquidoTwoColumnTitle });
      }

      if (this.shouldShowRetornoColumn) {
        columns.push({ key: "retorno", label: "Retorno" });
      }

      if (this.shouldShowAcertoLpColumn) {
        columns.push({ key: "acertoLp", label: "Acerto L.P" });
      }

      columns.push(
        { key: "bancaRecebeu", label: "Banca Recebeu(-)" },
        { key: "bancaPagou", label: "Banca Pagou(+)" },
        { key: "currentBalance", label: "Saldo Atual" },
      );

      return columns;
    },
  },
  async mounted() {
    this.loadVisibleColumns();
    this.loadTableZoom();
    document.addEventListener("click", this.handleColumnsMenuOutsideClick);
    this.loading = true;
    try {
      await this.loadCurrentUser();
      
      // Ao entrar na página, filtrar pela data do dia atual
      this.initializeDatesToToday();
      
      await this.searchRegions();

      if (this.selectedRegions.length > 0 && this.balanceDateBegin && this.balanceDateEnd) {
        this.searchBalance();
      } else {
        this.loading = false;
      }
    } catch (error) {
      this.loading = false;
      toast.error(handleErrorMessage(error));
    }
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleColumnsMenuOutsideClick);
  },
  watch: {
    currentSelectedRegion: {
      handler(newRegion, oldRegion) {
        // Quando a região ativa mudar, recarregar as regiões disponíveis
        this.updateSelectedRegions();
      },
      deep: true
    },

    currentSelectedBanca: {
      handler(newBanca, oldBanca) {
        // Quando a banca ativa mudar, recarregar as regiões disponíveis
        this.updateSelectedRegions();
      },
      deep: true
    },

    selectedRegions: {
      handler(newSelection, oldSelection) {
        if (!Array.isArray(newSelection)) return;
        if (newSelection && oldSelection && JSON.stringify(newSelection) !== JSON.stringify(oldSelection)) {
          this.balance = { regions: [], betAmount: 0 };
          // Filtrar automaticamente conforme o usuário adiciona/remove regiões
          if (newSelection.length > 0 && this.balanceDateBegin && this.balanceDateEnd) {
            this.searchBalance();
          }
        }
      },
      deep: true
    }
  },
  methods: {
    initializeDatesToToday() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const todayStr = `${year}-${month}-${day}`;
      this.balanceDateBegin = todayStr;
      this.balanceDateEnd = todayStr;
    },

    loadVisibleColumns() {
      try {
        const saved = localStorage.getItem(ENTRIES_COLUMNS_STORAGE_KEY);
        if (!saved) {
          this.visibleColumns = { ...DEFAULT_VISIBLE_COLUMNS };
          return;
        }
        const parsed = JSON.parse(saved);
        this.visibleColumns = {
          ...DEFAULT_VISIBLE_COLUMNS,
          ...(parsed && typeof parsed === "object" ? parsed : {}),
          region: true,
        };
      } catch {
        this.visibleColumns = { ...DEFAULT_VISIBLE_COLUMNS };
      }
    },

    saveVisibleColumns() {
      localStorage.setItem(
        ENTRIES_COLUMNS_STORAGE_KEY,
        JSON.stringify(this.visibleColumns),
      );
    },

    loadTableZoom() {
      try {
        const saved = parseInt(localStorage.getItem(TABLE_ZOOM_STORAGE_KEY), 10);
        if (!Number.isNaN(saved) && saved >= TABLE_ZOOM_MIN && saved <= TABLE_ZOOM_MAX) {
          this.tableZoom = saved;
          return;
        }
      } catch {
        // ignore
      }
      this.tableZoom = 100;
    },

    saveTableZoom() {
      localStorage.setItem(TABLE_ZOOM_STORAGE_KEY, String(this.tableZoom));
    },

    decreaseTableZoom() {
      if (this.tableZoom <= TABLE_ZOOM_MIN) return;
      this.tableZoom = Math.max(TABLE_ZOOM_MIN, this.tableZoom - TABLE_ZOOM_STEP);
      this.saveTableZoom();
    },

    increaseTableZoom() {
      if (this.tableZoom >= TABLE_ZOOM_MAX) return;
      this.tableZoom = Math.min(TABLE_ZOOM_MAX, this.tableZoom + TABLE_ZOOM_STEP);
      this.saveTableZoom();
    },

    resetTableZoom() {
      this.tableZoom = 100;
      this.saveTableZoom();
    },

    isColumnAllowedByBusiness(key) {
      switch (key) {
        case "retainedPrizes":
          return this.shouldShowRetainedPrizesColumn;
        case "liquidoTwo":
          return this.shouldShowLiquidoTwoColumn;
        case "retorno":
          return this.shouldShowRetornoColumn;
        case "acertoLp":
          return this.shouldShowAcertoLpColumn;
        default:
          return true;
      }
    },

    isColumnVisible(key) {
      if (!this.isColumnAllowedByBusiness(key)) {
        return false;
      }
      if (key === "region") {
        return true;
      }
      return this.visibleColumns[key] !== false;
    },

    toggleColumn(key) {
      if (key === "region") {
        return;
      }
      this.visibleColumns = {
        ...this.visibleColumns,
        [key]: !this.isColumnVisible(key),
      };
      this.saveVisibleColumns();
    },

    resetVisibleColumns() {
      this.visibleColumns = { ...DEFAULT_VISIBLE_COLUMNS };
      this.saveVisibleColumns();
    },

    closeColumnsMenu() {
      this.showColumnsMenu = false;
    },

    handleColumnsMenuOutsideClick(event) {
      if (!this.showColumnsMenu) {
        return;
      }
      const dropdown = this.$refs.columnsDropdown;
      if (dropdown && !dropdown.contains(event.target)) {
        this.closeColumnsMenu();
      }
    },

    // ============================================
    // MÉTODOS PARA DETERMINAR TIPO DE REGIÃO
    // ============================================
    
    // Determinar tipo de região/banca: 'normal', 'descarga', ou 'redescarga'
    getRegionType(region) {
      const fullRegion = this.regions.find(r => r.id === region.regionId);
      
      if (!fullRegion) {
        // Se não encontrou na lista, usar informações do balance
        if (region.isDescargaRegion) {
          return 'descarga';
        }
        return 'normal';
      }
      
      // IMPORTANTE: Verificar se a BANCA da região é redescarga
      // A banca tem a propriedade isRedescarga que define o tipo
      const banca = this.bancas.find(b => b.id === fullRegion.bancaId);
      
      // Se a banca for marcada como redescarga, todas as regiões dessa banca são redescarga
      if (banca && banca.isRedescarga) {
        return 'redescarga';
      }
      
      // Redescarga (fallback): onlyDescarga=true && descargaRegionId=null
      if (fullRegion.onlyDescarga && !fullRegion.descargaRegionId) {
        return 'redescarga';
      }
      
      // Descarga: onlyDescarga=true && descargaRegionId != null
      if (fullRegion.onlyDescarga && fullRegion.descargaRegionId) {
        return 'descarga';
      }
      
      // Normal: onlyDescarga=false
      return 'normal';
    },

    // Verificar se deve mostrar "Banca Recebeu" na tabela principal (Fechamento por Região)
    shouldShowBancaRecebeuInMainTable(region) {
      const type = this.getRegionType(region);
      return type === 'normal' || type === 'descarga' || type === 'redescarga';
    },

    // Verificar se deve mostrar "Banca Pagou" na tabela principal (Fechamento por Região)
    shouldShowBancaPagouInMainTable(region) {
      const type = this.getRegionType(region);
      return type === 'normal' || type === 'descarga' || type === 'redescarga';
    },

    shouldUseInvertedBancaStylingInMainTable(region) {
      return false;
    },

    // Verificar se deve mostrar a região na tabela Acerto L.P (embaixo)
    shouldShowRegionInAcertoLp(region) {
      const type = this.getRegionType(region);
      // Normal: lançamentos sem vendedor
      // Descarga: transações enviadas/recebidas da redescarga
      // Redescarga: não mostra embaixo
      return type === 'normal' || type === 'descarga';
    },

    // Obter valor de "Banca Recebeu" para a tabela principal (Fechamento por Região)
    getBancaRecebeuMainTable(region) {
      const type = this.getRegionType(region);
      if (type === 'normal' || type === 'descarga') {
        // Normal e Descarga: Banca Recebeu = credit (ENTRADA)
        // Descarga: recebeu da Região Normal → Descarga
        return region.credit || 0;
      }
      if (type === 'redescarga') {
        // Redescarga: Banca Recebeu = credit (ENTRADA)
        return region.credit || 0;
      }
      return 0;
    },

    // Obter valor de "Banca Pagou" para a tabela principal (Fechamento por Região)
    getBancaPagouMainTable(region) {
      const type = this.getRegionType(region);
      if (type === 'normal' || type === 'descarga') {
        // Normal e Descarga: Banca Pagou = debit (SAIDA)
        // Descarga: pagou enviando de Descarga → Região Normal
        return region.debit || 0;
      }
      if (type === 'redescarga') {
        // Redescarga: Banca Pagou = debit (SAIDA)
        return region.debit || 0;
      }
      return 0;
    },

    // Obter valor de "Banca Recebeu(+)" para a tabela Acerto L.P (embaixo)
    getBancaRecebeuAcertoLp(region) {
      const type = this.getRegionType(region);
      if (type === 'normal') {
        // Normal: creditLp (ENTRADA sem vendedor)
        return region.creditLp || 0;
      }
      if (type === 'descarga') {
        // Descarga: recebeu da Redescarga = creditLp
        return region.creditLp || 0;
      }
      return 0;
    },

    // Obter valor de "Banca Pagou(-)" para a tabela Acerto L.P (embaixo)
    getBancaPagouAcertoLp(region) {
      const type = this.getRegionType(region);
      if (type === 'normal') {
        // Normal: debitLp (SAIDA sem vendedor)
        return region.debitLp || 0;
      }
      if (type === 'descarga') {
        // Descarga: enviou para Redescarga = debitLp
        return region.debitLp || 0;
      }
      return 0;
    },
    
    
    async loadCurrentUser() {
      try {
        const userData = localStorage.getItem('user');
        if (userData) {
          this.currentUser = JSON.parse(userData);
        } else {
          toast.error("Usuário não encontrado. Faça login novamente.");
          this.$router.push('/login');
        }
      } catch (error) {
        this.currentUser = null;
        toast.error("Erro ao carregar dados do usuário");
      }
    },

    async loadUserResponsibleRegions() {
      try {
        if (!this.currentUser) return;
        
        if (this.isBanca) {
          // Para BANCA: buscar o BancaId do usuário e depois filtrar regiões
          const bancaResponse = await mainService.getUserBancaId(this.currentUser.id);
          this.userBancaId = bancaResponse.data;

          if (this.userBancaId) {
            // Buscar todas as regiões e filtrar por BancaId
            const allRegionsResponse = await mainService.searchRegions();
            const allRegions = allRegionsResponse.data || [];
            
            // Filtrar regiões que pertencem à banca do usuário
            const bancaRegions = allRegions.filter(region => region.bancaId === this.userBancaId);
            this.userResponsibleRegions = bancaRegions.map(region => region.id);
          } else {
            this.userResponsibleRegions = [];
            toast.error("Usuário BANCA não possui BancaId definido!");
          }
        } else {
          // Para GERENTE e COBRADOR: usar a API de regiões responsáveis
          const response = await mainService.getUserRegions(this.currentUser.id);
          this.userResponsibleRegions = response.data.map(region => region.id);
          
          // Incluir a região principal também, se existir
          if (this.currentUser.regionId && !this.userResponsibleRegions.includes(this.currentUser.regionId)) {
            this.userResponsibleRegions.push(this.currentUser.regionId);
          }
        }
      } catch (error) {
        this.userResponsibleRegions = [];
        toast.error("Erro ao carregar regiões do usuário");
      }
    },

    searchBalance() {
      const regionIds = [...new Set(
        this.selectedRegionsSafe
          .map(c => c?.id ?? c?.regionId)
          .filter(id => id != null && id !== '')
      )];
      if (regionIds.length === 0) {
        toast.warning("Selecione uma ou mais Regiões!");
        return;
      }
      if (this.balanceDateBegin == null || this.balanceDateBegin == "") {
        toast.warning("Selecione a data Inicial!");
        return;
      }
      if (this.balanceDateEnd == null || this.balanceDateEnd == "") {
        toast.warning("Selecione a data Final!");
        return;
      }
      
      this.loading = true;

      const dateBeginStr = this.balanceDateBegin + "T00:00:00";
      const dateEndStr = this.balanceDateEnd + "T23:59:59";
      
      if (this.balanceDateBegin > this.balanceDateEnd) {
        toast.error("A data inicial não pode ser maior que a data final!");
        this.loading = false;
        return;
      }
      
      let request = {
        regionIds,
        balanceDateBegin: dateBeginStr,
        balanceDateEnd: dateEndStr,
      };

      mainService
        .searchBalance(request)
        .then((response) => {
          this.balance = response.data;
        })
        .catch((error) => {
          toast.error(handleErrorMessage(error));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    
    async searchRegions() {
      try {
        const response = await mainService.searchRegions();
        this.regions = response.data || [];
        
        // Carregar bancas se ainda não foram carregadas (para filtro por banca)
        if (!this.bancas || this.bancas.length === 0) {
          await this.loadBancas();
        }
        
        // Aguardar carregamento das regiões do usuário se não for ADMIN
        if (!this.isAdmin) {
          await this.loadUserResponsibleRegions();
        }
        
        // Pré-preencher com a região ativa no select (se estiver nas opções) para facilitar o uso
        if (this.currentSelectedRegion && this.availableRegions.length > 0) {
          const activeInList = this.availableRegions.find(r => String(r.id) === String(this.currentSelectedRegion.id));
          this.selectedRegions = activeInList ? [activeInList] : [];
        } else {
          this.selectedRegions = [];
        }
      } catch (error) {
        toast.error(handleErrorMessage(error));
        this.regions = [];
      }
    },

    async loadBancas() {
      const response = await mainService.searchBancas({ isActive: true });
      this.bancas = response.data || [];
    },

    updateSelectedRegions() {
      // Ao trocar de região/banca: atualizar o select com a região ativa (se existir nas opções disponíveis)
      this.balance = { regions: [], betAmount: 0 };
      if (this.currentSelectedRegion && this.availableRegions.length > 0) {
        const activeInList = this.availableRegions.find(r => String(r.id) === String(this.currentSelectedRegion.id));
        this.selectedRegions = activeInList ? [activeInList] : [];
      } else {
        this.selectedRegions = [];
      }
    },
    
    setCurrencyString(value) {
      return jsFunctions.setCurrencyString(value);
    },
    
    openBets(regionId) {
      // Verificar se o usuário tem permissão para acessar esta região
      if (!this.isAdmin && !this.userResponsibleRegions.some(id => String(id) === String(regionId))) {
        toast.error("Você não tem permissão para acessar dados desta região!");
        return;
      }

      try {
        this.$router.push({
          path: "/entriesnumbers",
          query: {
            regionId: String(regionId),
            StartDate: `${this.balanceDateBegin}T00:00:00`,
            EndDate: `${this.balanceDateEnd}T23:59:59`,
          },
        });
      } catch (error) {
        toast.error("Erro ao abrir página de detalhes das apostas");
      }
    },

    calculateReturnValue(region) {
      // Para regiões de descarga: usar valor calculado no backend
      if (region.isDescargaRegion && region.returnValue !== undefined) {
        return region.returnValue || 0;
      }
      
      // Para regiões normais: retornar 0
      return 0;
    },
 
    calculateTotalReturnValue() {
      let totalReturnValue = 0;
      this.balance.regions.forEach(region => {
        totalReturnValue += this.calculateReturnValue(region);
      });
      return totalReturnValue;
    },

    calculateLiquidoTwo(region) {
      // Para regiões de descarga: usar valor calculado no backend (baseado em descargas recebidas)
      if (region.isDescargaRegion && region.liquidoTwo !== undefined) {
        return region.liquidoTwo || 0;
      }
      
      // Para regiões normais: L.P / Líquido 2 = apenas Líquido - Prêmios Pagos (NÃO somar Saldo Anterior)
      // Igual EntriesSeller: o Saldo Anterior só é considerado no Saldo Atual (total final)
      const net = region.net || 0;
      const totalAwarded = region.totalAwarded || 0;
      return net - totalAwarded;
    },

    calculateTotalLiquidoTwo() {
      let totalLiquidoTwo = 0;
      this.balance.regions.forEach(region => {
        totalLiquidoTwo += this.calculateLiquidoTwo(region);
      });
      return totalLiquidoTwo;
    },

    calculateAcertoLp(region) {
      // Para regiões de descarga: usar LPDescarga calculado no backend (baseado em descargas recebidas)
      if (region.isDescargaRegion && region.lpDescarga !== undefined) {
        return region.lpDescarga || 0;
      }
      
      // Para regiões normais: Acerto L.P = apenas Líquido - Prêmios (NÃO somar Saldo Anterior por linha)
      // O Saldo Anterior é considerado apenas no total final (Saldo Atual)
      const net = region.net || 0;
      const totalAwarded = region.totalAwarded || 0;
      return net - totalAwarded;
    },

    calculateTotalAcertoLp() {
      let totalAcertoLp = 0;
      this.balance.regions.forEach(region => {
        totalAcertoLp += this.calculateAcertoLp(region);
      });
      return totalAcertoLp;
    },

    // Totais para "Banca Recebeu" e "Banca Pagou" na tabela principal
    calculateTotalBancaRecebeuMain() {
      let total = 0;
      this.balance.regions.forEach(region => {
        total += this.getBancaRecebeuMainTable(region);
      });
      return total;
    },

    calculateTotalBancaPagouMain() {
      let total = 0;
      this.balance.regions.forEach(region => {
        total += this.getBancaPagouMainTable(region);
      });
      return total;
    },

    // Totais para "Banca Recebeu" e "Banca Pagou" na tabela Acerto L.P
    calculateTotalBancaRecebeuAcertoLp() {
      let total = 0;
      this.balance.regions.forEach(region => {
        total += this.getBancaRecebeuAcertoLp(region);
      });
      return total;
    },

    calculateTotalBancaPagouAcertoLp() {
      let total = 0;
      this.balance.regions.forEach(region => {
        total += this.getBancaPagouAcertoLp(region);
      });
      return total;
    },

    openTransactionsModal(region, transactionType, isLpTable = false) {
      this.modalRegionId = region.regionId;
      this.modalRegionName = region.regionDescription;
      this.modalTransactionType = transactionType;
      this.modalIsLpTable = isLpTable;
      this.modalIsDescargaRegion = this.getRegionType(region) === 'descarga';
      this.showTransactionsModal = true;
    },

    closeTransactionsModal() {
      this.showTransactionsModal = false;
      this.modalRegionId = null;
      this.modalRegionName = '';
      this.modalTransactionType = 'credit';
      this.modalIsDescargaRegion = false;
      this.modalIsLpTable = false;
    },

    openPrizeModal(region) {
      if (!region || (region.totalAwarded || 0) <= 0) return;
      this.showSalesModal = false;
      this.ticketsModalRegionId = region.regionId;
      this.ticketsModalEntityName = region.regionDescription || '';
      this.showPrizeModal = true;
    },

    openSalesModal(region) {
      if (!region || (region.bet || 0) <= 0) return;
      this.showPrizeModal = false;
      this.ticketsModalRegionId = region.regionId;
      this.ticketsModalEntityName = region.regionDescription || '';
      this.showSalesModal = true;
    },

    closeTicketsModal() {
      this.showPrizeModal = false;
      this.showSalesModal = false;
      this.ticketsModalRegionId = null;
      this.ticketsModalEntityName = '';
    },

    closePrizeModal() {
      this.closeTicketsModal();
    },

    printReport() {
      if (!this.balance || !this.balance.regions || this.balance.regions.length === 0) {
        toast.warning("Não há dados para imprimir");
        return;
      }

      const printWindow = window.open("", "_blank");
      const printContent = this.generatePrintHTML();
      
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    },

    generatePrintHTML() {
      const selectedRegionsText = this.selectedRegionsSafe.map(r => r.name || r.label || r.displayName).join(", ");
      const regions = this.balance?.regions || [];
      
      let tableRows = "";
      regions.forEach(region => {
        tableRows += `
          <tr>
            ${this.isColumnVisible("region") ? `<td>${region.regionDescription || ""}</td>` : ""}
            ${this.isColumnVisible("previousBalance") ? `<td style="text-align: right;">${this.setCurrencyString(region.previousBalance || 0)}</td>` : ""}
            ${this.isColumnVisible("bet") ? `<td style="text-align: right;">${this.setCurrencyString(region.bet || 0)}</td>` : ""}
            ${this.isColumnVisible("comission") ? `<td style="text-align: right;">${this.setCurrencyString(region.comission || 0)}</td>` : ""}
            ${this.isColumnVisible("net") ? `<td style="text-align: right;">${this.setCurrencyString(region.net || 0)}</td>` : ""}
            ${this.isColumnVisible("retainedPrizes") ? `<td style="text-align: right;">${this.setCurrencyString(region.retainedPrizes || 0)}</td>` : ""}
            ${this.isColumnVisible("totalAwarded") ? `<td style="text-align: right;">${this.setCurrencyString(region.totalAwarded || 0)}</td>` : ""}
            ${this.isColumnVisible("liquidoTwo") ? `<td style="text-align: right;">${this.setCurrencyString(this.calculateLiquidoTwo(region))}</td>` : ""}
            ${this.isColumnVisible("retorno") ? `<td style="text-align: right;">${this.setCurrencyString(this.calculateReturnValue(region))}</td>` : ""}
            ${this.isColumnVisible("acertoLp") ? `<td style="text-align: right;">${this.setCurrencyString(this.calculateAcertoLp(region))}</td>` : ""}
            ${this.isColumnVisible("bancaRecebeu") ? `<td style="text-align: right;">${this.setCurrencyString(this.getBancaRecebeuMainTable(region))}</td>` : ""}
            ${this.isColumnVisible("bancaPagou") ? `<td style="text-align: right;">${this.setCurrencyString(this.getBancaPagouMainTable(region))}</td>` : ""}
            ${this.isColumnVisible("currentBalance") ? `<td style="text-align: right; font-weight: bold;">${this.setCurrencyString(region.currentBalance || 0)}</td>` : ""}
          </tr>
        `;
      });

      const totalCells = this.getTotalExportRow(true)
        .map((cell, idx) => {
          const isFirst = idx === 0;
          return `<td style="text-align: ${isFirst ? "left" : "right"}; font-weight: bold;">${cell}</td>`;
        })
        .join("");
      const totalRowHtml = `<tr class="total-row">${totalCells}</tr>`;

      let headerRow = `
        ${this.isColumnVisible("region") ? "<th>Região</th>" : ""}
        ${this.isColumnVisible("previousBalance") ? "<th>Saldo Anterior</th>" : ""}
        ${this.isColumnVisible("bet") ? "<th>Vendas(+)</th>" : ""}
        ${this.isColumnVisible("comission") ? "<th>Comissão(-)</th>" : ""}
        ${this.isColumnVisible("net") ? "<th>Líquido</th>" : ""}
        ${this.isColumnVisible("retainedPrizes") ? "<th>Prêmios Retidos</th>" : ""}
        ${this.isColumnVisible("totalAwarded") ? "<th>Prêmios(-)</th>" : ""}
        ${this.isColumnVisible("liquidoTwo") ? `<th>${this.getLiquidoTwoColumnTitle}</th>` : ""}
        ${this.isColumnVisible("retorno") ? "<th>Retorno</th>" : ""}
        ${this.isColumnVisible("acertoLp") ? "<th>Acerto L.P</th>" : ""}
        ${this.isColumnVisible("bancaRecebeu") ? "<th>Banca Recebeu(+)</th>" : ""}
        ${this.isColumnVisible("bancaPagou") ? "<th>Banca Pagou(-)</th>" : ""}
        ${this.isColumnVisible("currentBalance") ? "<th>Saldo Atual</th>" : ""}
      `;

      return `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Relatório de Fechamento Coletivo</title>
          <style>
            @media print {
              @page { margin: 1cm; }
              body { margin: 0; }
            }
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              color: #333;
            }
            h1 {
              text-align: center;
              color: #2c3e50;
              margin-bottom: 10px;
            }
            .report-info {
              text-align: center;
              margin-bottom: 20px;
              color: #666;
              font-size: 14px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
              font-size: 12px;
            }
            th {
              background-color: #e5e5e5;
              padding: 10px;
              text-align: center;
              border: 1px solid #ddd;
              font-weight: bold;
            }
            td {
              padding: 8px;
              border: 1px solid #ddd;
              text-align: center;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .total-row td {
              background-color: #e8eef7;
              font-weight: bold;
              border-top: 2px solid #333;
            }
            .footer {
              margin-top: 30px;
              text-align: center;
              font-size: 11px;
              color: #666;
            }
          </style>
        </head>
        <body>
          <h1>Relatório de Fechamento Coletivo</h1>
          <div class="report-info">
            <p><strong>Período:</strong> ${this.balanceDateBegin} até ${this.balanceDateEnd}</p>
            <p><strong>Regiões:</strong> ${selectedRegionsText}</p>
            <p><strong>Data de geração:</strong> ${jsFunctions.setDateTimeDisplay(new Date().toISOString())}</p>
          </div>
          <table>
            <thead>
              <tr>
                ${headerRow}
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
            <tfoot>
              ${totalRowHtml}
            </tfoot>
          </table>
          <div class="footer">
            <p>Total de registros: ${regions.length}</p>
          </div>
        </body>
        </html>
      `;
    },

    getTotalExportRow(formatCurrency = true) {
      const money = (v) =>
        formatCurrency ? this.setCurrencyString(v ?? 0) : Number(v ?? 0);
      const b = this.balance || {};
      const row = [];
      if (this.isColumnVisible("region")) row.push("Total");
      if (this.isColumnVisible("previousBalance")) row.push(money(b.previousBalanceAmount));
      if (this.isColumnVisible("bet")) row.push(money(b.betAmount));
      if (this.isColumnVisible("comission")) row.push(money(b.comissionAmount));
      if (this.isColumnVisible("net")) row.push(money(b.netAmount));
      if (this.isColumnVisible("retainedPrizes")) row.push(money(b.retainedPrizes));
      if (this.isColumnVisible("totalAwarded")) row.push(money(b.totalAwarded));
      if (this.isColumnVisible("liquidoTwo")) row.push(money(this.calculateTotalLiquidoTwo()));
      if (this.isColumnVisible("retorno")) row.push(money(this.calculateTotalReturnValue()));
      if (this.isColumnVisible("acertoLp")) row.push(money(this.calculateTotalAcertoLp()));
      if (this.isColumnVisible("bancaRecebeu")) row.push(money(this.calculateTotalBancaRecebeuMain()));
      if (this.isColumnVisible("bancaPagou")) row.push(money(this.calculateTotalBancaPagouMain()));
      if (this.isColumnVisible("currentBalance")) row.push(money(b.currentBalanceAmount));
      return row;
    },

    exportToPDF() {
      if (!this.balance || !this.balance.regions || this.balance.regions.length === 0) {
        toast.warning("Não há dados para exportar");
        return;
      }

      try {
        const doc = new jsPDF("landscape", "mm", "a4");
        
        // Título
        doc.setFontSize(16);
        doc.text("Relatório de Fechamento Coletivo", 14, 15);
        
        // Informações do relatório
        doc.setFontSize(10);
        doc.text(`Período: ${this.balanceDateBegin} até ${this.balanceDateEnd}`, 14, 25);
        const selectedRegionsText = this.selectedRegionsSafe.map(r => r.name || r.label || r.displayName).join(", ");
        const regionsText = doc.splitTextToSize(`Regiões: ${selectedRegionsText}`, 260);
        doc.text(regionsText, 14, 30);
        doc.text(`Data de geração: ${jsFunctions.setDateTimeDisplay(new Date().toISOString())}`, 14, 35);
        
        // Preparar dados da tabela
        const tableColumns = [];
        if (this.isColumnVisible("region")) tableColumns.push("Região");
        if (this.isColumnVisible("previousBalance")) tableColumns.push("Saldo Anterior");
        if (this.isColumnVisible("bet")) tableColumns.push("Vendas(+)");
        if (this.isColumnVisible("comission")) tableColumns.push("Comissão(-)");
        if (this.isColumnVisible("net")) tableColumns.push("Líquido");
        if (this.isColumnVisible("retainedPrizes")) tableColumns.push("Prêmios Retidos");
        if (this.isColumnVisible("totalAwarded")) tableColumns.push("Prêmios(-)");
        if (this.isColumnVisible("liquidoTwo")) tableColumns.push(this.getLiquidoTwoColumnTitle);
        if (this.isColumnVisible("retorno")) tableColumns.push("Retorno");
        if (this.isColumnVisible("acertoLp")) tableColumns.push("Acerto L.P");
        if (this.isColumnVisible("bancaRecebeu")) tableColumns.push("Banca Recebeu(+)");
        if (this.isColumnVisible("bancaPagou")) tableColumns.push("Banca Pagou(-)");
        if (this.isColumnVisible("currentBalance")) tableColumns.push("Saldo Atual");
        
        const tableData = (this.balance?.regions || []).map(region => {
          const row = [];
          if (this.isColumnVisible("region")) row.push(region.regionDescription || "");
          if (this.isColumnVisible("previousBalance")) row.push(this.setCurrencyString(region.previousBalance || 0));
          if (this.isColumnVisible("bet")) row.push(this.setCurrencyString(region.bet || 0));
          if (this.isColumnVisible("comission")) row.push(this.setCurrencyString(region.comission || 0));
          if (this.isColumnVisible("net")) row.push(this.setCurrencyString(region.net || 0));
          if (this.isColumnVisible("retainedPrizes")) row.push(this.setCurrencyString(region.retainedPrizes || 0));
          if (this.isColumnVisible("totalAwarded")) row.push(this.setCurrencyString(region.totalAwarded || 0));
          if (this.isColumnVisible("liquidoTwo")) row.push(this.setCurrencyString(this.calculateLiquidoTwo(region)));
          if (this.isColumnVisible("retorno")) row.push(this.setCurrencyString(this.calculateReturnValue(region)));
          if (this.isColumnVisible("acertoLp")) row.push(this.setCurrencyString(this.calculateAcertoLp(region)));
          if (this.isColumnVisible("bancaRecebeu")) row.push(this.setCurrencyString(this.getBancaRecebeuMainTable(region)));
          if (this.isColumnVisible("bancaPagou")) row.push(this.setCurrencyString(this.getBancaPagouMainTable(region)));
          if (this.isColumnVisible("currentBalance")) row.push(this.setCurrencyString(region.currentBalance || 0));
          return row;
        });
        
        // Adicionar tabela usando autoTable diretamente
        autoTable(doc, {
          head: [tableColumns],
          body: tableData,
          foot: [this.getTotalExportRow(true)],
          startY: 40,
          styles: { fontSize: 7, cellPadding: 2 },
          headStyles: { fillColor: [229, 229, 229], textColor: [0, 0, 0] },
          footStyles: {
            fillColor: [232, 238, 247],
            textColor: [0, 0, 0],
            fontStyle: "bold",
          },
          alternateRowStyles: { fillColor: [249, 249, 249] },
          margin: { left: 14, right: 14 },
          tableWidth: "wrap",
          showFoot: "lastPage",
        });
        
        // Salvar PDF
        const fileName = `Fechamento_Coletivo_${this.balanceDateBegin}_${this.balanceDateEnd}.pdf`;
        doc.save(fileName);
        toast.success("PDF exportado com sucesso!");
      } catch (error) {
        console.error("Erro ao exportar PDF:", error);
        toast.error("Erro ao exportar PDF");
      }
    },

    exportToExcel() {
      if (!this.balance || !this.balance.regions || this.balance.regions.length === 0) {
        toast.warning("Não há dados para exportar");
        return;
      }

      try {
        // Preparar cabeçalhos
        const headers = [];
        if (this.isColumnVisible("region")) headers.push("Região");
        if (this.isColumnVisible("previousBalance")) headers.push("Saldo Anterior");
        if (this.isColumnVisible("bet")) headers.push("Vendas(+)");
        if (this.isColumnVisible("comission")) headers.push("Comissão(-)");
        if (this.isColumnVisible("net")) headers.push("Líquido");
        if (this.isColumnVisible("retainedPrizes")) headers.push("Prêmios Retidos");
        if (this.isColumnVisible("totalAwarded")) headers.push("Prêmios(-)");
        if (this.isColumnVisible("liquidoTwo")) headers.push(this.getLiquidoTwoColumnTitle);
        if (this.isColumnVisible("retorno")) headers.push("Retorno");
        if (this.isColumnVisible("acertoLp")) headers.push("Acerto L.P");
        if (this.isColumnVisible("bancaRecebeu")) headers.push("Banca Recebeu(+)");
        if (this.isColumnVisible("bancaPagou")) headers.push("Banca Pagou(-)");
        if (this.isColumnVisible("currentBalance")) headers.push("Saldo Atual");
        
        // Preparar dados da tabela
        const tableData = (this.balance?.regions || []).map(region => {
          const row = [];
          if (this.isColumnVisible("region")) row.push(region.regionDescription || "");
          if (this.isColumnVisible("previousBalance")) row.push(region.previousBalance || 0);
          if (this.isColumnVisible("bet")) row.push(region.bet || 0);
          if (this.isColumnVisible("comission")) row.push(region.comission || 0);
          if (this.isColumnVisible("net")) row.push(region.net || 0);
          if (this.isColumnVisible("retainedPrizes")) row.push(region.retainedPrizes || 0);
          if (this.isColumnVisible("totalAwarded")) row.push(region.totalAwarded || 0);
          if (this.isColumnVisible("liquidoTwo")) row.push(this.calculateLiquidoTwo(region));
          if (this.isColumnVisible("retorno")) row.push(this.calculateReturnValue(region));
          if (this.isColumnVisible("acertoLp")) row.push(this.calculateAcertoLp(region));
          if (this.isColumnVisible("bancaRecebeu")) row.push(this.getBancaRecebeuMainTable(region));
          if (this.isColumnVisible("bancaPagou")) row.push(this.getBancaPagouMainTable(region));
          if (this.isColumnVisible("currentBalance")) row.push(region.currentBalance || 0);
          return row;
        });
        
        // Criar workbook
        const wb = XLSX.utils.book_new();
        
        // Preparar dados completos: informações + cabeçalho + dados
        const allData = [
          ["Relatório de Fechamento Coletivo"],
          [""],
          ["Período:", `${this.balanceDateBegin} até ${this.balanceDateEnd}`],
          ["Regiões:", this.selectedRegionsSafe.map(r => r.name || r.label || r.displayName).join(", ")],
          ["Data de geração:", jsFunctions.setDateTimeDisplay(new Date().toISOString())],
          [""],
          headers, // Cabeçalho da tabela
          ...tableData // Dados da tabela
        ];
        
        // Criar worksheet com todos os dados
        const ws = XLSX.utils.aoa_to_sheet(allData);
        
        // Definir largura das colunas
        const colWidths = headers.map(() => ({ wch: 15 }));
        ws["!cols"] = colWidths;
        
        // Adicionar worksheet ao workbook
        XLSX.utils.book_append_sheet(wb, ws, "Fechamento Coletivo");
        
        // Salvar arquivo
        const fileName = `Fechamento_Coletivo_${this.balanceDateBegin}_${this.balanceDateEnd}.xlsx`;
        XLSX.writeFile(wb, fileName);
        toast.success("Excel exportado com sucesso!");
      } catch (error) {
        console.error("Erro ao exportar Excel:", error);
        toast.error("Erro ao exportar Excel");
      }
    }
  },
};
</script>

<style scoped>
/* IMPORTANTE: Garantir que os dropdowns dos MultiSelect sobreponham todos os elementos */
/* Remover overflow: hidden dos containers para permitir que o dropdown apareça */
.entries-container .filters-card {
  overflow: visible !important;
}

.filters-content {
  overflow: visible !important;
}

.filters-grid {
  overflow: visible !important;
}

.input-group {
  overflow: visible !important;
  position: relative !important;
  z-index: 1 !important;
}

.multiselect-container {
  position: relative !important;
  z-index: 10 !important;
}

/* Quando o multiselect estiver ativo/aberto, aumentar o z-index */
.multiselect-container:has(.multiselect--active) {
  z-index: 1000 !important;
}

/* Table container com scroll horizontal */
.table-container {
  overflow-x: auto;
  max-width: 100%;
}

/* Tabela moderna com texto sem quebra de linha */
.modern-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.95rem;
  white-space: nowrap;
}

.modern-table th {
  background: #e5e5e5;
  color: var(--text-primary);
  padding: 1rem 1.5rem;
  text-align: center;
  font-weight: 600;
  border-bottom: 2px solid var(--border-color);
  white-space: nowrap;
}

[data-theme="dark"] .modern-table th {
  background: #2d2d2d;
  color: #f4f4f5;
}

.modern-table td {
  padding: 0.75rem 1.5rem;
  text-align: center;
  border-bottom: 1px solid var(--border-color);
  color: var(--text-primary);
  white-space: nowrap;
}

.data-row:nth-child(even) {
  background: rgba(0, 0, 0, 0.02);
}

[data-theme="dark"] .data-row:nth-child(even) {
  background: rgba(255, 255, 255, 0.02);
}

.data-row:hover {
  background: rgba(96, 165, 250, 0.1);
}

/* Células específicas */
.region-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: flex-start;
  white-space: nowrap;
}

.region-link {
  color: var(--accent-color);
  cursor: pointer;
  text-decoration: underline;
}

.region-link:hover {
  color: var(--accent-hover);
}

/* Valores monetários */
.currency-value {
  font-family: 'Consolas', 'Monaco', monospace;
  font-weight: 500;
  color: var(--text-primary);
}

.currency-positive {
  font-family: 'Consolas', 'Monaco', monospace;
  font-weight: 500;
  color: #10b981 !important;
}

.currency-negative {
  font-family: 'Consolas', 'Monaco', monospace;
  font-weight: 500;
  color: #ef4444 !important;
}

.currency-muted {
  font-family: 'Consolas', 'Monaco', monospace;
  font-weight: 500;
  color: var(--text-muted, #9ca3af) !important;
}

/* Valores clicáveis */
.clickable-value {
  cursor: pointer;
  text-decoration: underline;
  text-decoration-style: dotted;
  text-underline-offset: 3px;
  transition: all 0.2s ease;
  position: relative;
}

.clickable-value:hover {
  opacity: 0.8;
  transform: scale(1.05);
  text-decoration-style: solid;
}

.clickable-value:active {
  transform: scale(0.98);
}

.balance-value {
  font-family: 'Consolas', 'Monaco', monospace;
  font-weight: 600;
  color: var(--text-primary);
}

.balance-value.final-balance {
  font-size: 1.05rem;
  background: rgba(96, 165, 250, 0.1);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

/* Total row */
.total-row {
  background: rgba(96, 165, 250, 0.1);
  font-weight: 600;
}

.total-row td {
  border-top: 2px solid var(--accent-color);
  font-weight: 600;
}

.total-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
  color: var(--accent-color);
  font-weight: 600;
}

/* Responsividade para telas pequenas */
@media (max-width: 768px) {
  .modern-table {
    font-size: 0.8rem;
  }
  
  .modern-table th,
  .modern-table td {
    padding: 0.5rem 0.75rem;
  }
}

@media (max-width: 480px) {
  .modern-table th,
  .modern-table td {
    padding: 0.375rem 0.5rem;
  }
}

/* Botões de ação — sobrescreve .btn-action global (32x32 ícone) */
.results-card {
  overflow: visible !important;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  position: relative;
  z-index: 5;
  overflow: visible !important;
}

.card-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
  position: relative;
  z-index: 6;
}

.table-zoom-controls {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.2rem 0.45rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--bg-secondary, var(--card-background));
}

.btn-zoom {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--text-primary);
  cursor: pointer;
  transition: background 0.15s ease;
}

.btn-zoom:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.06);
}

.btn-zoom:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.zoom-label {
  min-width: 3rem;
  text-align: center;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text-primary);
  cursor: pointer;
  user-select: none;
}

.zoom-label:hover {
  color: var(--primary-color, #3b82f6);
}

.btn-action {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: auto !important;
  height: auto !important;
  min-width: unset !important;
  min-height: 36px;
  padding: 0.5rem 1rem !important;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  color: white;
  white-space: nowrap;
  box-sizing: border-box;
}

.btn-action i {
  font-size: 0.875rem;
  line-height: 1;
  flex-shrink: 0;
}

.btn-action:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.btn-action:active {
  transform: translateY(0);
}

.btn-print {
  background-color: #6b7280;
}

.btn-print:hover {
  background-color: #4b5563;
}

.btn-pdf {
  background-color: #dc2626;
}

.btn-pdf:hover {
  background-color: #b91c1c;
}

.btn-excel {
  background-color: #16a34a;
}

.btn-excel:hover {
  background-color: #15803d;
}

.columns-dropdown {
  position: relative;
  z-index: 50;
  flex: 0 0 auto;
}

.btn-columns {
  background-color: #2563eb;
}

.btn-columns:hover {
  background-color: #1d4ed8;
}

.columns-caret {
  font-size: 0.75rem;
  margin-left: 0.15rem;
}

.columns-menu {
  position: absolute;
  top: calc(100% + 0.4rem);
  right: 0;
  z-index: 1000;
  min-width: 220px;
  max-height: 360px;
  overflow-y: auto;
  background: var(--card-background, #fff);
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 8px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.22);
  padding: 0.5rem;
}

[data-theme="dark"] .columns-menu {
  background: #1f1f1f;
  border-color: #3f3f46;
}

.columns-menu-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.35rem 0.5rem 0.6rem;
  border-bottom: 1px solid var(--border-color, #e5e7eb);
  margin-bottom: 0.35rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-primary, #111);
}

.columns-reset {
  border: none;
  background: transparent;
  color: #2563eb;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}

.columns-reset:hover {
  text-decoration: underline;
}

.columns-menu-item {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.45rem 0.5rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  color: var(--text-primary, #111);
  user-select: none;
}

.columns-menu-item:hover {
  background: rgba(37, 99, 235, 0.08);
}

.columns-menu-item.locked {
  opacity: 0.7;
  cursor: default;
}

.columns-menu-item input {
  width: 15px;
  height: 15px;
  accent-color: #2563eb;
}

@media (max-width: 768px) {
  .card-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .card-actions {
    width: 100%;
  }
  
  .btn-action {
    flex: 1 1 auto;
    justify-content: center;
    width: auto !important;
    height: auto !important;
  }

  .columns-dropdown {
    flex: 1 1 auto;
  }

  .columns-dropdown .btn-action {
    width: 100% !important;
  }

  .columns-menu {
    left: 0;
    right: 0;
    min-width: 0;
  }
}
</style>
